import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/auth/TenantContext';
import { meetsRoleRequirement, REQUIRED_ROLES } from '@/auth/roles';
import { NoAccessPage } from '@/auth/NoAccessPage';

const useStyles = makeStyles({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
});

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Roles that grant access. Defaults to REQUIRED_ROLES (roles.ts — empty = open access). */
  requiredRoles?: readonly string[];
}

/**
 * Top-level auth/role gate (SPA-Auth Standard rule 5). The state ORDER is the
 * point — no-access must NEVER render while roles are still resolving:
 *
 *   1. auth state loading               → spinner
 *   2. unauthenticated                  → sign-in (/login)
 *   3. authenticated, roles loading     → spinner (access token resolving — NEVER no-access)
 *   4. authenticated, requirement unmet → NoAccessPage (account switch + diagnostics)
 *   5. authenticated + requirement met  → the app
 *
 * Roles come from `useTenant()` — the UNION of ID-token and API ACCESS-token
 * roles (rule 1). This app is currently open-access (REQUIRED_ROLES is empty,
 * so step 4 never fires); the gate is fully wired for the day it isn't.
 */
export function ProtectedRoute({ children, requiredRoles = REQUIRED_ROLES }: ProtectedRouteProps) {
  const styles = useStyles();
  const { isAuthenticated, isLoading } = useAuth();
  const { roles, rolesStatus } = useTenant();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Rule 5 — roles still resolving: spinner, NEVER the no-access screen.
  if (rolesStatus === 'loading') {
    return (
      <div className={styles.loading} data-testid="roles-loading">
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  // Resolved + requirement unmet (only possible when requiredRoles is non-empty).
  if (!meetsRoleRequirement(roles, requiredRoles)) {
    return <NoAccessPage />;
  }

  return <>{children}</>;
}
